/* eslint-disable no-useless-catch */
/* eslint-disable react/prop-types */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  useMap,
  Popup,
  Marker
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import GeotiffLayer from './GeotiffLayerFarmLevel';
import Legend from 'components/common/map/Legend';
import Model from '../monitoring/Model';
import { FiInfo } from 'react-icons/fi';

const MapCenter = React.memo(({ firstCoordinate }) => {
  const map = useMap();
  const prevCoordinateRef = useRef();

  useEffect(() => {
    if (
      firstCoordinate &&
      JSON.stringify(firstCoordinate) !==
        JSON.stringify(prevCoordinateRef.current)
    ) {
      const geoJsonLayer = L.geoJSON(firstCoordinate);
      const bounds = geoJsonLayer.getBounds();

      if (bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: [10, 10],
          maxZoom: 15
        });
      } else {
        console.warn('Invalid bounds for the first polygon');
      }

      prevCoordinateRef.current = firstCoordinate;
    }
  }, [firstCoordinate, map]);

  return null;
});

const getPolygonCentroid = feature => {
  const geometry = feature.geometry;

  if (geometry.type === 'Polygon') {
    const polygon = L.polygon(geometry.coordinates);
    const center = polygon.getBounds().getCenter();
    return [center.lng, center.lat];
  } else if (geometry.type === 'MultiPolygon') {
    const centroids = geometry.coordinates.map(polygonCoords => {
      const polygon = L.polygon(polygonCoords);
      const center = polygon.getBounds().getCenter();
      return [center.lat, center.lng];
    });

    const avgLat =
      centroids.reduce((sum, centroid) => sum + centroid[0], 0) /
      centroids.length;
    const avgLng =
      centroids.reduce((sum, centroid) => sum + centroid[1], 0) /
      centroids.length;

    return [avgLng, avgLat];
  }

  return null;
};

const MultiPolygonMap = React.memo(
  ({ data, coordinates, legendData, variant, label, info }) => {
    const sortedYears = useMemo(() => {
      if (Array.isArray(variant)) {
        return variant.sort((a, b) => b - a);
      } else if (typeof variant === 'object') {
        return Object.keys(variant).sort((a, b) => b - a);
      }
      return [];
    }, [variant]);

    const defaultYear = sortedYears[0] || '';
    const defaultMonth = useMemo(
      () =>
        typeof variant === 'object' && Array.isArray(variant[defaultYear])
          ? variant[defaultYear][1]
          : null,
      [variant, defaultYear]
    );

    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    const [layerVisible, setLayerVisible] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const handleShowModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    const handleYearChange = useCallback(
      event => {
        const newYear = event.target.value;
        setSelectedYear(newYear);
        const availableMonths = variant[newYear] || [];

        if (availableMonths.includes(selectedMonth)) {
          setSelectedMonth(selectedMonth);
        } else {
          setSelectedMonth(availableMonths[0] || '');
        }
      },
      [variant, selectedMonth]
    );

    const handleMonthChange = useCallback(event => {
      setSelectedMonth(event.target.value);
    }, []);

    const selectedYearMonth = useMemo(
      () =>
        selectedMonth && selectedYear
          ? `${selectedYear}::${selectedMonth}`
          : selectedYear,
      [selectedMonth, selectedYear]
    );

    const renderMarkers = useMemo(() => {
      return coordinates.map((feature, index) => {
        if (index === 0) return null;
        const position = getPolygonCentroid(feature);
        if (!position) return null;
        return (
          <Marker
            key={feature.properties.id}
            position={position}
            eventHandlers={{
              mouseover: e => e.target.openPopup(),
              mouseout: e => e.target.closePopup()
            }}
          >
            <Popup autoClose={false} closeButton={false} closeOnClick={false}>
              <div
                style={{ display: 'flex', flexDirection: 'column', margin: 0 }}
              >
                <p className="m-0">
                  Farmland Id: {feature.properties.project_id}
                </p>
                <p className="m-0">
                  Farmland: {feature.properties.project_name}
                </p>
                <p className="m-0">Farmer: {feature.properties.farmer_name}</p>
                <p className="m-0">Area: {feature.properties.project_area}</p>
                {data[feature.properties.id]?.[
                  `${selectedYear}::${selectedMonth}`
                ]?.overlay_info && (
                  <p className="m-0">
                    Info:{' '}
                    {
                      data[feature.properties.id][
                        `${selectedYear}::${selectedMonth}`
                      ].overlay_info
                    }
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        );
      });
    }, [coordinates, data, selectedYear, selectedMonth]);

    const renderGeotiffLayers = useMemo(() => {
      return coordinates.map((feature, index) => {
        if (
          data[feature.properties.id] == undefined ||
          data[feature.properties.id][selectedYearMonth] == undefined
        )
          return null;
        return (
          <GeotiffLayer
            key={feature.properties.id}
            url={data[feature.properties.id][selectedYearMonth]?.url}
            title={`Feature ${feature.properties.id}`}
            coordinates={feature.geometry.coordinates}
            layerVisible={layerVisible}
            legendData={legendData}
            isParent={index === 0}
            childFeatures={index === 0 ? coordinates.slice(1) : undefined}
            zIndex={index === 0 ? 1 : 100 + index}
            clipPolygon={feature.geometry.coordinates}
          />
        );
      });
    }, [
      coordinates,
      data,
      selectedYear,
      selectedMonth,
      layerVisible,
      legendData
    ]);

    const mapBounds = useMemo(() => {
      if (coordinates.length > 0) {
        const firstFeature = L.geoJSON(coordinates[0]);
        return firstFeature.getBounds();
      }
      return null;
    }, [coordinates]);

    const endElement = useMemo(
      () => (
        <Row className="g-0 align-items-center justify-content-end">
          {Array.isArray(variant) ? (
            variant.length > 1 && (
              <Col className="col-auto">
                <Form.Select
                  className="ps-2 pe-5 py-0"
                  value={selectedYear}
                  name="leftSide"
                  onChange={handleYearChange}
                >
                  {variant.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )
          ) : (
            <Col className="col-auto">
              <Form.Select
                className="ps-2 pe-5 py-0"
                value={selectedYear}
                name="leftSide"
                onChange={handleYearChange}
              >
                {sortedYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}
          <Col className="text-center p-0 d-flex align-align-items-center">
            <span className="m-auto px-2"></span>
          </Col>
          {selectedMonth && (
            <Col className="col-auto">
              <Form.Select
                className="ps-2 pe-5 py-0"
                value={selectedMonth}
                name="leftSide"
                onChange={handleMonthChange}
              >
                {variant[selectedYear].map(month => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}
        </Row>
      ),
      [
        variant,
        selectedYear,
        selectedMonth,
        sortedYears,
        handleYearChange,
        handleMonthChange
      ]
    );

    return (
      <div>
        <Card className="h-100 overflow-hidden card">
          <FalconCardHeader
            title={label || ''}
            titleTag="h5"
            light={true}
            endEl={endElement}
            // desc={'description'}
            infoBtn={!isEmpty(info) && <FiInfo onClick={handleShowModal} />}
          />
          <Card.Body className="h-100 p-0" dir="ltr">
            <MapContainer
              bounds={mapBounds}
              zoom={10}
              style={{ height: '410px', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>'
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_KEY}`}
              />
              {coordinates.length > 0 && (
                <GeoJSON
                  data={coordinates}
                  style={() => ({
                    color: 'white',
                    fillOpacity: 0,
                    weight: 3
                  })}
                />
              )}
              {renderMarkers}
              {renderGeotiffLayers}
              {!isEmpty(legendData) && (
                <Legend
                  indexScale={legendData}
                  title={'vegetation'}
                  setLayerVisible={setLayerVisible}
                />
              )}
              {/* Uncomment if MapCenter is needed */}
              {/* <MapCenter firstCoordinate={coordinates[0]} /> */}
            </MapContainer>
          </Card.Body>
          <Card.Footer className="bg-white py-2 card-footer-style">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <div style={{ flex: 1 }}>
                {coordinates.map((feature, index) =>
                  index === 0 ? (
                    <p
                      key={index}
                      style={{ fontSize: '0.8rem', fontWeight: '600' }}
                      className="mb-0"
                    >
                      {feature.properties.project_name}:{' '}
                      {data[feature.properties.id]?.[selectedYearMonth]?.info ||
                        'No data available'}
                    </p>
                  ) : null
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}
              >
                {coordinates.map((feature, index) =>
                  index !== 0 ? (
                    <p
                      key={index}
                      style={{ fontSize: '0.8rem', fontWeight: '600' }}
                      className="mb-0"
                    >
                      {feature.properties.project_name}:{' '}
                      {data[feature.properties.id]?.[selectedYearMonth]?.info ||
                        'No data available'}
                    </p>
                  ) : null
                )}
              </div>
            </div>
          </Card.Footer>
        </Card>
        <Model
          showModal={showModal}
          handleCloseModal={handleCloseModal}
          description={info || {}}
          title={label}
        />
      </div>
    );
  }
);

export default MultiPolygonMap;
